/**
 * Usage Dashboard Component
 *
 * Displays comprehensive usage metrics with charts and breakdowns
 */

import * as React from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useCurrentUsage, useUsageByModel } from '@/hooks/useUsage';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { DollarSign, Zap, TrendingUp, AlertCircle } from 'lucide-react';

interface UsageDashboardProps {
  workspaceId: string;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export function UsageDashboard({ workspaceId }: UsageDashboardProps): React.JSX.Element {
  const { data: currentUsage, isLoading: usageLoading } = useCurrentUsage(workspaceId);
  const { data: usageByModel, isLoading: modelLoading } = useUsageByModel(workspaceId);

  const isLoading = usageLoading || modelLoading;

  if (isLoading) {
    return <UsageDashboardSkeleton />;
  }

  if (!currentUsage) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No usage data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Prepare data for charts
  const modelChartData = usageByModel
    ? usageByModel.map((model) => ({
        name: model.modelId.split('/').pop() || model.modelId,
        tokens: model.totalTokens,
        cost: model.totalCost,
        requests: model.requestCount,
      }))
    : [];

  const pieChartData = usageByModel
    ? usageByModel.map((model) => ({
        name: model.modelId.split('/').pop() || model.modelId,
        value: model.totalCost,
      }))
    : [];

  const totalCost = currentUsage.totalCost;
  const totalTokens = currentUsage.totalTokens;
  const percentUsed = currentUsage.percentUsed || 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalCost.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">Current billing period</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tokens</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalTokens.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Input + Output tokens</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Models Used</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{usageByModel?.length || 0}</div>
            <p className="text-xs text-muted-foreground">Different AI models</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Period</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">
              {new Date(currentUsage.periodStart).toLocaleDateString()}
            </div>
            <p className="text-xs text-muted-foreground">
              to {new Date(currentUsage.periodEnd).toLocaleDateString()}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Budget Progress */}
      {currentUsage.limit && (
        <Card>
          <CardHeader>
            <CardTitle>Budget Usage</CardTitle>
            <CardDescription>
              ${totalCost.toFixed(2)} of ${currentUsage.limit.toFixed(2)} used
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Progress value={percentUsed} className="h-2" />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{percentUsed.toFixed(1)}% used</span>
                <span className="font-medium">
                  ${(currentUsage.limit - totalCost).toFixed(2)} remaining
                </span>
              </div>
              {percentUsed >= 90 && (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  <span>Approaching budget limit</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Cost by Model - Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Cost by Model</CardTitle>
            <CardDescription>Spending breakdown across AI models</CardDescription>
          </CardHeader>
          <CardContent>
            {modelChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={modelChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      if (name === 'cost') return `$${value.toFixed(4)}`;
                      if (name === 'tokens') return value.toLocaleString();
                      return value;
                    }}
                  />
                  <Legend />
                  <Bar dataKey="cost" fill="#3b82f6" name="Cost ($)" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No model usage data
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cost Distribution - Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Cost Distribution</CardTitle>
            <CardDescription>Proportional spending by model</CardDescription>
          </CardHeader>
          <CardContent>
            {pieChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name}: ${((percent || 0) * 100).toFixed(0)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieChartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number) => `$${value.toFixed(4)}`}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No cost distribution data
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Model Details Table */}
      <Card>
        <CardHeader>
          <CardTitle>Model Usage Details</CardTitle>
          <CardDescription>Detailed breakdown of usage by model</CardDescription>
        </CardHeader>
        <CardContent>
          {usageByModel && usageByModel.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2 font-medium">Model</th>
                    <th className="text-left p-2 font-medium">Provider</th>
                    <th className="text-right p-2 font-medium">Requests</th>
                    <th className="text-right p-2 font-medium">Input Tokens</th>
                    <th className="text-right p-2 font-medium">Output Tokens</th>
                    <th className="text-right p-2 font-medium">Total Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {usageByModel.map((model, index) => (
                    <tr key={index} className="border-b last:border-0">
                      <td className="p-2 font-mono text-sm">{model.modelId}</td>
                      <td className="p-2">
                        <Badge variant="outline">{model.provider || 'N/A'}</Badge>
                      </td>
                      <td className="p-2 text-right">{model.requestCount}</td>
                      <td className="p-2 text-right">{model.inputTokens.toLocaleString()}</td>
                      <td className="p-2 text-right">{model.outputTokens.toLocaleString()}</td>
                      <td className="p-2 text-right font-medium">
                        ${model.totalCost.toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No model usage data available
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function UsageDashboardSkeleton(): React.JSX.Element {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="space-y-0 pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-32 mb-2" />
              <Skeleton className="h-3 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-2 w-full mb-2" />
          <div className="flex justify-between">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-24" />
          </div>
        </CardContent>
      </Card>
      <div className="grid gap-4 md:grid-cols-2">
        {[...Array(2)].map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[300px] w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
